
import {
    getCountySlugCandidatesFromName,
    normalizeCountySlug,
} from '@/lib/shopPlansRouting';

const countiesCache = new Map<string, Array<{ value: string; label: string }>>();
let allCountiesData: Array<{ state: string; name: string; fips_code: string }> | null = null;


function parseCSVLine(line: string): { state: string; name: string; fips_code: string } | null {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
            if (inQuotes && nextChar === '"') {

                current += '"';
                i++;
            } else {

                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {

            parts.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    parts.push(current.trim());

    if (parts.length < 4) return null;


    const state = parts[2]?.replace(/^"|"$/g, '').trim();
    const name = parts[3]?.replace(/^"|"$/g, '').trim();
    const fips_code = parts[1]?.replace(/^"|"$/g, '').trim();

    if (!state || !name) return null;

    return { state, name, fips_code };
}


async function loadCountiesData(): Promise<Array<{ state: string; name: string; fips_code: string }>> {
    if (allCountiesData) return allCountiesData;

    try {
        const response = await fetch('/counties.csv');
        const text = await response.text();
        const lines = text.split('\n').filter((line) => line.trim());


        const data = lines
            .slice(1)
            .map(parseCSVLine)
            .filter((item): item is { state: string; name: string; fips_code: string } => item !== null);


        const uniqueMap = new Map<string, { state: string; name: string; fips_code: string }>();
        data.forEach((item) => {
            const key = `${item.state}-${item.name}`;
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, item);
            }
        });

        allCountiesData = Array.from(uniqueMap.values());
        return allCountiesData;
    } catch (error) {
        console.error('Failed to load counties data:', error);
        return [];
    }
}


export async function getCountiesByState(
    stateAbbreviation: string
): Promise<Array<{ value: string; label: string }>> {
    if (!stateAbbreviation) return [];


    if (countiesCache.has(stateAbbreviation)) {
        return countiesCache.get(stateAbbreviation)!;
    }


    const data = await loadCountiesData();


    const stateCounties = data.filter((item) => item.state === stateAbbreviation);
    const uniqueCounties = new Map<string, string>();

    stateCounties.forEach((item) => {
        if (!uniqueCounties.has(item.name)) {
            uniqueCounties.set(item.name, item.fips_code);
        }
    });


    const options = Array.from(uniqueCounties.entries())
        .map(([name, fips_code]) => ({
            value: fips_code,
            label: name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));


    countiesCache.set(stateAbbreviation, options);

    return options;
}


export async function getCountyCodeByName(
    stateAbbreviation: string,
    countyName: string
): Promise<string | null> {
    if (!stateAbbreviation || !countyName) return null;

    const options = await getCountiesByState(stateAbbreviation);
    const county = options.find((opt) => opt.label.toLowerCase() === countyName.toLowerCase());
    return county ? county.value : null;
}


export async function getCountyNameByCode(
    stateAbbreviation: string,
    countyCode: string
): Promise<string | null> {
    if (!stateAbbreviation || !countyCode) return null;

    const options = await getCountiesByState(stateAbbreviation);
    const county = options.find((opt) => opt.value === countyCode);
    return county ? county.label : null;
}

export async function getCountyCodeBySlug(
    stateAbbreviation: string,
    countySlug: string
): Promise<string | null> {
    if (!stateAbbreviation || !countySlug) return null;

    const targetSlug = normalizeCountySlug(countySlug);
    if (!targetSlug) return null;

    const options = await getCountiesByState(stateAbbreviation);

    for (const county of options) {
        if (typeof county.label !== 'string') continue;

        const candidates = getCountySlugCandidatesFromName(county.label, stateAbbreviation);
        if (candidates.includes(targetSlug)) {
            return county.value;
        }
    }

    return null;
}

export async function getCountySlugByCode(
    stateAbbreviation: string,
    countyCode: string
): Promise<string | null> {
    const countyName = await getCountyNameByCode(stateAbbreviation, countyCode);
    if (!countyName) return null;

    const candidates = getCountySlugCandidatesFromName(countyName, stateAbbreviation);
    return candidates[0] ?? null;
}
